import { Base64FileUriUploadModelWithAttribution, ImageAttributionViewModel } from "../common";
import {
	FrequentlyAskedQuestionItemViewModel,
	HubItemCategoryNodeKind,
	HubItemElementKind,
	HubItemMediaKind,
	HubItemRowLayoutKind,
	ImageFocalPointModel,
	SharePointLinkedDocumentModel,
	WebDavLinkedDocumentModel
} from "./common";
import { HubItemCategoryModel } from "./hub";

export type HubItemLayoutComposerViewModel = {
	rows: readonly HubItemRowComposerViewModel[];
};

export type HubItemRowComposerViewModel = {
	layout: HubItemRowLayoutKind;
	columns: readonly HubItemColumnComposerViewModel[];
};

export type HubItemColumnComposerViewModel = {
	elements: readonly HubItemElementComposerViewModel[];
};

export type HorizontalLineElementComposerViewModel = {
	id: number | null;
	kind: HubItemElementKind.HorizontalLine;
};

export enum HubImageMediaKind {
	BlobId = 1,
	Inline = 2
}

export type BlobIdImageElementComposerViewModel = {
	id: number | null;
	kind: HubItemElementKind.Image;
	imageKind: HubImageMediaKind.BlobId;
	blobId: string;
	name: string;
	attribution: ImageAttributionViewModel | null;
};

export type InlineImageElementComposerViewModel = {
	id: number | null;
	kind: HubItemElementKind.Image;
	imageKind: HubImageMediaKind.Inline;
	dataUri: string;
	name: string;
	attribution: ImageAttributionViewModel | null;
};

export type ImageElementComposerViewModel =
	| BlobIdImageElementComposerViewModel
	| InlineImageElementComposerViewModel;

export type ParagraphElementComposerViewModel = {
	id: number | null;
	kind: HubItemElementKind.Paragraph;
	html: string | null;
};

export type SubtitleElementComposerViewModel = {
	id: number | null;
	kind: HubItemElementKind.Subtitle;
	text: string;
};

export type VideoElementComposerViewModel = {
	id: number | null;
	kind: HubItemElementKind.Video;
	url: string;
};

export type EmbedElementComposerViewModel = {
	id: number | null;
	kind: HubItemElementKind.Embed;
	code: string;
};

export type FrequentlyAskedQuestionElementComposerViewModel = {
	id: number | null;
	kind: HubItemElementKind.FrequentlyAskedQuestion;
	items: readonly FrequentlyAskedQuestionItemViewModel[];
};

export type HubItemElementComposerViewModel =
	| HorizontalLineElementComposerViewModel
	| ImageElementComposerViewModel
	| ParagraphElementComposerViewModel
	| SubtitleElementComposerViewModel
	| VideoElementComposerViewModel
	| EmbedElementComposerViewModel
	| FrequentlyAskedQuestionElementComposerViewModel;

export type NoneHubMediaComposerViewModel = {
	mediaKind: HubItemMediaKind.None;
};

export type BlobIdImageHubMediaComposerViewModel = {
	mediaKind: HubItemMediaKind.Image;
	imageKind: HubImageMediaKind.BlobId;
	imageId: string;
	imageName: string;
	focalPoint: ImageFocalPointModel;
	attribution: ImageAttributionViewModel | null;
};

export type InlineImageHubMediaComposerViewModel = {
	mediaKind: HubItemMediaKind.Image;
	imageKind: HubImageMediaKind.Inline;
	dataUri: string;
	imageName: string;
	focalPoint: ImageFocalPointModel;
	attribution: ImageAttributionViewModel | null;
};

export type ImageHubMediaComposerViewModel =
	| BlobIdImageHubMediaComposerViewModel
	| InlineImageHubMediaComposerViewModel;

export type VideoHubMediaComposerViewModel = {
	mediaKind: HubItemMediaKind.Video;
	videoUrl: string;
};

export type EmbedHubItemMediaComposerViewModel = {
	mediaKind: HubItemMediaKind.Embed;
	embedCode: string;
};

export type HubItemMediaComposerViewModel =
	| NoneHubMediaComposerViewModel
	| ImageHubMediaComposerViewModel
	| VideoHubMediaComposerViewModel
	| EmbedHubItemMediaComposerViewModel;

export type ExistingHubItemImages = {
	coverImage: BlobIdImageHubMediaComposerViewModel | null;
	layoutImages: Map<number, BlobIdImageElementComposerViewModel>;
};

export type NoneHubMediaSaveModel = {
	mediaKind: HubItemMediaKind.None;
};

export type ImageHubMediaSaveModel = {
	mediaKind: HubItemMediaKind.Image;
	image: OptionalModel<Base64FileUriUploadModelWithAttribution>;
	focalPoint: ImageFocalPointModel;
};

export type VideoHubMediaSaveModel = {
	mediaKind: HubItemMediaKind.Video;
	videoUrl: string;
};

export type EmbedHubMediaSaveModel = {
	mediaKind: HubItemMediaKind.Embed;
	embedCode: string;
};

export type HubItemMediaSaveModel =
	| NoneHubMediaSaveModel
	| ImageHubMediaSaveModel
	| VideoHubMediaSaveModel
	| EmbedHubMediaSaveModel;


export type HubItemLayoutSaveModel = {
	rows: readonly HubItemRowSaveModel[];
};

export type HubItemRowSaveModel = {
	layout: HubItemRowLayoutKind;
	columns: readonly HubItemColumnSaveModel[];
};

export type HubItemColumnSaveModel = {
	elements: readonly HubItemElementSaveModel[];
};

export type HorizontalLineElementSaveModel = {
	id: number | null;
	kind: HubItemElementKind.HorizontalLine;
};

export type ImageElementSaveModel = {
	id: number | null;
	kind: HubItemElementKind.Image;
	image: OptionalModel<Base64FileUriUploadModelWithAttribution>;
};

export type ParagraphElementSaveModel = {
	id: number | null;
	kind: HubItemElementKind.Paragraph;
	html: string;
};

export type SubtitleElementSaveModel = {
	id: number | null;
	kind: HubItemElementKind.Subtitle;
	text: string;
};

export type VideoElementSaveModel = {
	id: number | null;
	kind: HubItemElementKind.Video;
	url: string;
};

export type EmbedElementSaveModel = {
	id: number | null;
	kind: HubItemElementKind.Embed;
	code: string;
};

export type FrequentlyAskedQuestionItemSaveModel = {
	question: string;
	answer: string;
};

export type FrequentlyAskedQuestionElementSaveModel = {
	id: number | null;
	kind: HubItemElementKind.FrequentlyAskedQuestion;
	items: readonly FrequentlyAskedQuestionItemSaveModel[];
};

export type HubItemElementSaveModel =
	| HorizontalLineElementSaveModel
	| ImageElementSaveModel
	| ParagraphElementSaveModel
	| SubtitleElementSaveModel
	| EmbedElementSaveModel
	| VideoElementSaveModel
	| FrequentlyAskedQuestionElementSaveModel;

export type HubLinkedDocumentSaveModel = {
	name: string;
} & (
	| SharePointLinkedDocumentModel
	| WebDavLinkedDocumentModel
);

export type HubHierarchyListCategoryNodeModel = {
	kind: HubItemCategoryNodeKind.Category;
	category: HubItemCategoryModel;
};

export type HubHierarchyListGroupNodeModel = {
	kind: HubItemCategoryNodeKind.Group;
	name: string;
	children: readonly HubHierarchyListNodeModel[];
};

export type HubHierarchyListNodeModel =
	| HubHierarchyListGroupNodeModel
	| HubHierarchyListCategoryNodeModel;
